"use client";

import { ChevronDown, ChevronUp } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import getUserId from "src/utils/getUserId";

const Footer = () => {
  const [openSections, setOpenSections] = useState<{ [key: number]: boolean }>(
    {},
  );
  const { t, ready } = useTranslation(); // const userId = getUserId();
  const [userId, setUserId] = useState<number | null>(null);

  useEffect(() => {
    setUserId(getUserId()); // Ensure userId is only set on the client
  }, []);

  const toggleSection = (index: number) => {
    setOpenSections((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const socialLinks = [
    {
      icon: "/images/facebook.svg",
      name: "Facebook",
      href: "https://www.facebook.com/profile.php?id=61573938462598  ",
    },
    {
      icon: "/images/instagram-logo.svg",
      name: "Instagram",
      href: "https://www.instagram.com/chedmedapp/",
    },
    {
      icon: "/images/youtube.svg",
      name: "Youtube",
      href: "https://www.youtube.com/@Chedmed-app",
    },
    {
      icon: "/images/tiktok.svg",
      name: "Tiktok",
      href: "https://www.tiktok.com/@chedmedapp",
    },
  ];

  const footerSections = [
    {
      title: ready ? t("links") : "Links",
      links: [
        { text: ready ? t("home") : "Home", href: "/" },
        {
          text: ready ? t("category") : "Category",
          href: "/#category",
        },
        { text: ready ? t("favorite") : "Favorite", href: "/favorite-items" },
        // Render link only when userId is available
        ...(userId
          ? [
            {
              text: ready ? t("orders") : "Orders",
              href: `/settings/account/my-orders?_id=${userId}&section=brought&status=All`,
            },
          ]
          : []),
      ],
    },
    {
      title: ready ? t("help") : "Help",
      links: [
        { text: t("Privacy Policy"), href: "/privacy-policy" },
        { text: t("Terms and Conditions"), href: "/terms-condition" },
        {
          text: ready ? t("return_refund_policy") : "Return & Refund Policy",
          href: "/return-refund-policy",
        },
        { text: ready ? t("about_us") : "About Us", href: "/about-us" },
      ],
    },
  ];

  return (
    <footer className="mt-32 flex w-full flex-col items-center self-stretch overflow-hidden bg-[#1D2C38] pb-6 pt-12 max-md:mt-10 max-md:max-w-full">
      <div className="flex w-full flex-col items-start justify-start gap-y-5 px-8 max-md:max-w-full md:flex-row md:justify-between md:gap-5 md:px-28">
        <div className="flex flex-col gap-y-2 text-lg text-white">
          <img
            src="/images/assets/checkmed-logo-white.png"
            alt="chedmed-logo"
            className="aspect-[2.68] w-[150px] max-w-full object-contain"
          />
          <h1 className="mt-3 text-xs md:mt-8 md:text-base">
            Chedmed202@gmail.com
          </h1>
          <p className="text-xs md:text-base">(+202) 900000009</p>
          <p className="mt-5 text-xs md:mt-8 md:text-base">
            123 Avenue Mohammed V, Marrakesh, <br /> Marrakesh-Safi, Morocco,
            40000.
          </p>
        </div>

        {footerSections.map((section, i) => (
          <div
            key={i}
            className="mt-3.5 flex w-full flex-col text-white md:w-1/6"
          >
            <div
              className="flex w-full items-center justify-between border-b border-b-black text-base font-bold md:cursor-default md:border-none md:text-2xl text-[#FF9C26]"
              onClick={() => toggleSection(i)}
            >
              {section?.title}{" "}
              <span className="block md:hidden">
                {openSections[i] ? <ChevronUp /> : <ChevronDown />}
              </span>
            </div>
            {openSections[i] && (
              <div className="mt-11 flex w-full flex-col text-sm max-md:mt-10 md:text-lg">
                {section.links.map((link, index) => (
                  <Link
                    href={link?.href}
                    key={index}
                    className="mt-6 first:mt-0"
                    scroll={false}
                    onClick={(e) => {
                      if (link.href === "/#category") {
                        e.preventDefault(); // Prevent default navigation
                        document.getElementById("category")?.scrollIntoView({
                          behavior: "smooth",
                        });
                      }
                    }}
                  >
                    {link.text}
                  </Link>
                ))}
              </div>
            )}
            <div className="mt-11 hidden w-full flex-col text-sm max-md:mt-10 md:flex md:text-lg">
              {section.links.map((link, index) => (
                <Link href={link?.href} key={index} className="mt-6 first:mt-0">
                  {link.text}
                </Link>
              ))}
            </div>
          </div>
        ))}

        <div className="mt-3.5 flex flex-col">
          <div className="text-base font-bold text-[#FF9C26] md:text-2xl">
            {ready ? t("social_links") : "Social Links"}
          </div>
          <div className="mt-5 flex w-full items-center justify-start gap-x-5 text-lg text-white md:mt-11 md:flex-col md:items-start md:justify-center md:gap-x-0 md:whitespace-nowrap md:max-md:mt-10">
            {socialLinks.map((link, i) => (
              <Link
                href={link?.href}
                key={i}
                className="flex items-center gap-4 first:mt-0 md:mt-6"
              >
                <div className="flex items-center justify-center rounded-full p-1">
                  <img
                    loading="lazy"
                    src={link.icon}
                    alt={`${link.name} icon`}
                    className="aspect-square w-6 object-contain invert md:my-auto md:shrink-0 md:self-stretch"
                  />
                </div>
                <p className="my-auto hidden self-stretch md:block">
                  {link.name}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-5 w-full border border-gray-400 md:mt-20" />
      <div className="mt-6 flex w-full justify-between gap-x-5 px-5 max-md:max-w-full md:max-w-[1200px] md:px-0">
        <div className="my-auto hidden items-center gap-8 md:flex">
          {socialLinks.map((link, i) => (
            <Link
              href={link?.href}
              key={i}
              className="flex items-center gap-4 first:mt-0"
            >
              <img
                loading="lazy"
                src={link.icon}
                alt={`${link.name}-icon`}
                className="my-auto aspect-square w-8 shrink-0 self-stretch fill-black object-contain invert"
              />
            </Link>
          ))}
        </div>

        <div className="flex w-full items-center justify-between text-xs text-white md:w-fit md:gap-x-7">
          <div className="flex w-[47%] items-center justify-center gap-x-4 rounded-lg bg-black p-3 px-6 hover:cursor-pointer">
            <img
              loading="lazy"
              src="https://cdn.builder.io/api/v1/image/assets/TEMP/69960f1bb37e8d46a2ca758e5d4d586942cf98e4498de7253c296ace075ed158?placeholderIfAbsent=true&apiKey=e597e4c45b0c4799aed7ecec6c3f8c22"
              alt="icon"
              className="aspect-[0.84] w-6 shrink-0 fill-white object-contain md:w-9"
            />
            <div className="flex flex-col items-start justify-start">
              <p className="text-[10px]">Download on the</p>
              <h3 className="font-medium md:text-xl md:font-semibold">
                App Store
              </h3>
            </div>
          </div>
          <div className="flex w-[47%] items-center justify-center gap-x-4 rounded-lg bg-black p-3 px-6 hover:cursor-pointer md:w-[223px]">
            <img
              loading="lazy"
              src="https://cdn.builder.io/api/v1/image/assets/TEMP/807257ac39890c5bd2605a2386fb62eb6808c772f9d19351d4112d98c0a20153?placeholderIfAbsent=true&apiKey=e597e4c45b0c4799aed7ecec6c3f8c22"
              alt="icon"
              className="aspect-[0.84] w-6 shrink-0 fill-white object-contain md:w-9"
            />
            <div className="flex flex-col items-start justify-start">
              <p className="text-[10px]">Download on the</p>
              <h3 className="font-medium md:text-xl md:font-semibold">
                Google Play
              </h3>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
